import React, { FC, useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import Button from '../../../../components/common/Button';
import Input from '../../../../components/common/Input';
import Confirmation from '../../../../components/elements/Confirmation';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useModal } from '../../../../hooks/useModal';
import { useAppSelector } from '../../../../hooks/useSelector';
import { IDuelState } from '../../../../lib/interfaces/IDuelSettings';
import { getDuel } from '../../../../store/selectors/settings.selector';
import { settingsActions } from '../../../../store/slices/settings.slice';
import { teamActions } from '../../../../store/slices/team.slice';

import { DUEL_ABORT_TEXT, DUEL_CONFIRMATION_DESCRIPTION, DUEL_CONFIRMATION_TEXT } from './config';
import * as S from './styles';

interface IProps {
  toggleFlyout: () => void;
}

const DuelSettings: FC<IProps> = ({ toggleFlyout }) => {
  const settings = useAppSelector(getDuel);
  const [isActiveSetTime, setIsActiveSetTime] = useState(settings.isTimeSelected);
  const { isActive, toggleHandler: toggleConfirmation } = useModal({ handler: toggleFlyout });
  const dispatch = useAppDispatch();
  const { register, handleSubmit, setValue } = useForm<IDuelState>({ defaultValues: settings });

  const toggleActive = useCallback(() => {
    setIsActiveSetTime((prev) => !prev);
    setValue('player1.min', settings.player1.min);
    setValue('player1.sec', settings.player1.sec);
    setValue('player2.min', settings.player2.min);
    setValue('player2.sec', settings.player2.sec);
  }, [
    setValue,
    settings.player1.min,
    settings.player1.sec,
    settings.player2.min,
    settings.player2.sec,
  ]);

  const onConfirm = useCallback(() => {
    dispatch(teamActions.resetTeam());
    toggleConfirmation();
  }, [dispatch, toggleConfirmation]);

  const onSubmit: SubmitHandler<IDuelState> = useCallback(
    (data) => {
      if (!data.isTimeSelected) {
        dispatch(settingsActions.resetDuel());
        dispatch(teamActions.updateTimerKeys());
      } else {
        dispatch(settingsActions.setDuel(data));

        if (
          !(data.player1.min === settings.player1.min && data.player1.sec === settings.player1.sec)
        ) {
          dispatch(teamActions.updateTimerPlayer1());
        }

        if (
          !(data.player2.min === settings.player2.min && data.player2.sec === settings.player2.sec)
        ) {
          dispatch(teamActions.updateTimerPlayer2());
        }

        if (data.player1.min === 0 && data.player1.sec === 0) {
          dispatch(teamActions.onCompleteGame({ player: 'player1' }));
        } else if (data.player2.min === 0 && data.player2.sec === 0) {
          dispatch(teamActions.onCompleteGame({ player: 'player2' }));
        }
      }
      toggleFlyout();
    },
    [
      dispatch,
      settings.player1.min,
      settings.player1.sec,
      settings.player2.min,
      settings.player2.sec,
      toggleFlyout,
    ],
  );

  return (
    <>
      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <Input
          variant="toggle"
          label="SET TIME"
          {...register('isTimeSelected')}
          onChange={(e) => {
            void register('isTimeSelected').onChange(e);
            toggleActive();
          }}
          $isBold
        />

        <S.TimeSettingsContainer $isActive={isActiveSetTime}>
          <Input
            variant="time"
            label="Player 1"
            minProps={{
              ...register('player1.min', { min: 0, valueAsNumber: true }),
              defaultValue: settings.player1.min,
              min: 0,
            }}
            secProps={{
              ...register('player1.sec', { min: 0, valueAsNumber: true }),
              defaultValue: settings.player1.sec,
              min: 0,
            }}
          />

          <Input
            variant="time"
            label="Player 2"
            minProps={{
              ...register('player2.min', { min: 0, valueAsNumber: true }),
              defaultValue: settings.player2.min,
              min: 0,
            }}
            secProps={{
              ...register('player2.sec', { min: 0, valueAsNumber: true }),
              defaultValue: settings.player2.sec,
              min: 0,
            }}
          />
        </S.TimeSettingsContainer>

        <S.ButtonsContainer>
          <Button
            variant="outline"
            $isRed
            $size="large"
            onClick={toggleConfirmation}
            type="button"
          >
            finish game
          </Button>
          <Button
            variant="primary"
            $isRed
            $size="large"
            type="submit"
          >
            apply
          </Button>
        </S.ButtonsContainer>
      </S.Form>
      <Confirmation
        description={DUEL_CONFIRMATION_DESCRIPTION}
        confirmText={DUEL_CONFIRMATION_TEXT}
        abortText={DUEL_ABORT_TEXT}
        title="finish game"
        isActive={isActive}
        onConfirm={onConfirm}
        onClose={toggleConfirmation}
        onAbort={toggleConfirmation}
      />
    </>
  );
};

export default DuelSettings;
